import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { CONTACT_EMAIL } from 'src/constants/misc';
import { startScroll, stopScroll } from 'src/utils/smoothScroll';

import Button from '../Button';
import ContactForm from '../ContactForm';
import { CheckIcon, CloseIcon } from '../Icons';
import JoinGroupForm from '../JoinGroupForm';
import Orb from '../Orb';

import style from './DemoModal.module.scss';

const CLOSE_DURATION = 450;
const FOCUSABLE = 'button, input, select, textarea, a[href]';

// Modal content per page: texts key in common.json and the form
const VARIANTS = {
  demo: { texts: 'demo', eyebrow: true, Form: ContactForm },
  veterinaro: { texts: 'joinGroup', eyebrow: false, Form: JoinGroupForm }
};

const DemoModalContext = createContext({ openDemo: () => {}, setVariant: () => {} });

export const useDemoModal = () => useContext(DemoModalContext).openDemo;

// Lets a page pick which modal its buttons open
export const useDemoVariant = (variant) => {
  const { setVariant } = useContext(DemoModalContext);

  useEffect(() => {
    setVariant(variant);
    return () => setVariant('demo');
  }, [variant]);
};

export function DemoButton({ children, ...props }) {
  const openDemo = useDemoModal();

  return (
    <Button
      arrow
      onClick={(e) => openDemo(e.currentTarget)}
      {...props}
    >
      {children}
    </Button>
  );
}

export function DemoModalProvider({ children }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState('closed');
  const [hasOpened, setHasOpened] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [variant, setVariant] = useState('demo');
  const modalRef = useRef(null);
  const openerRef = useRef(null);
  const closeTimer = useRef(0);
  const submitted = useRef(false);

  const openDemo = useCallback((opener) => {
    openerRef.current = opener || document.activeElement;
    clearTimeout(closeTimer.current);
    document.documentElement.classList.add('modal-open');
    stopScroll();
    setHasOpened(true);
    setPhase('opening');
  }, []);

  const closeDemo = useCallback(() => {
    document.documentElement.classList.remove('modal-open');
    startScroll();
    setPhase('closing');
    closeTimer.current = setTimeout(() => {
      setPhase('closed');
      // Show an empty form next time after a successful submit
      if (submitted.current) {
        submitted.current = false;
        setFormKey((key) => key + 1);
      }
    }, CLOSE_DURATION);
    openerRef.current?.focus?.({ preventScroll: true });
  }, []);

  // Start the enter transition once the modal is visible, then focus the first field
  useEffect(() => {
    if (phase === 'opening') {
      const raf = requestAnimationFrame(() => setPhase('open'));
      return () => cancelAnimationFrame(raf);
    }
    if (phase === 'open') {
      const focus = setTimeout(() => modalRef.current.querySelector('input')?.focus({ preventScroll: true }), 60);
      return () => clearTimeout(focus);
    }
  }, [phase]);

  const context = useMemo(() => ({ openDemo, setVariant }), [openDemo]);
  const { texts, eyebrow, Form } = VARIANTS[variant];

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeDemo();
      return;
    }
    if (e.key !== 'Tab') return;

    const focusable = [...modalRef.current.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <DemoModalContext value={context}>
      {children}
      <div
        ref={modalRef}
        className={cn(style.modal, phase === 'open' && style.open)}
        hidden={phase === 'closed'}
        onKeyDown={handleKeyDown}
        data-lenis-prevent
      >
        <div
          className={style.backdrop}
          onClick={closeDemo}
        />
        <div
          className={style.dialog}
          role='dialog'
          aria-modal='true'
          aria-labelledby='dmTitle'
        >
          <button
            className={style.close}
            type='button'
            aria-label={t('demo.close')}
            onClick={closeDemo}
          >
            <CloseIcon />
          </button>
          <aside className={style.side}>
            {hasOpened && (
              <Orb
                className={style.orb}
                count={900}
                scale={0.3}
                glow={0.9}
              />
            )}
            {eyebrow && (
              <p className='eyebrow'>
                <span className='dot' />
                {t(`${texts}.eyebrow`)}
              </p>
            )}
            <h2
              className={style.title}
              id='dmTitle'
            >
              {t(`${texts}.title`)} <span className='serif-i accent-v'>{t(`${texts}.titleAccent`)}</span>
            </h2>
            <ul className={style.list}>
              {t(`${texts}.points`, { returnObjects: true }).map((point) => (
                <li key={point}>
                  <i>
                    <CheckIcon />
                  </i>
                  {point}
                </li>
              ))}
            </ul>
            <p className={style.mail}>
              {t('demo.mail')}{' '}
              <a
                className='sel'
                href={`mailto:${CONTACT_EMAIL}`}
              >
                {CONTACT_EMAIL}
              </a>
            </p>
          </aside>
          <div className={style.main}>
            <Form
              key={`${variant}-${formKey}`}
              active={phase === 'open'}
              onSuccess={() => (submitted.current = true)}
              onClose={closeDemo}
            />
          </div>
        </div>
      </div>
    </DemoModalContext>
  );
}
